import { useEffect, useRef } from 'react';

import { tokenAtual, tenantSelecionado, WS_BASE } from './api';
import type { EventoWs } from './types';

/**
 * Assina o canal de eventos do tenant.
 *
 * Reconecta com backoff. O evento `resync` (o gateway avisou que o painel ficou
 * para trás) é repassado como qualquer outro — quem consome decide recarregar
 * por REST.
 */
export function useEventos(
  ativo: boolean,
  aoEvento: (evento: EventoWs) => void,
  aoMudarConexao?: (conectado: boolean) => void,
) {
  // Handler em ref: assim trocar o callback não reabre o socket.
  const handler = useRef(aoEvento);
  handler.current = aoEvento;
  const status = useRef(aoMudarConexao);
  status.current = aoMudarConexao;

  const tenant = tenantSelecionado();
  const token = tokenAtual();

  useEffect(() => {
    if (!ativo || !token) return;

    let socket: WebSocket | null = null;
    let tentativa = 0;
    let timer: number | undefined;
    let encerrado = false;

    const conectar = () => {
      if (encerrado) return;

      const url = new URL(`${WS_BASE}/api/v1/ws`);
      url.searchParams.set('token', token);
      if (tenant) url.searchParams.set('tenant', tenant);

      socket = new WebSocket(url.toString());

      socket.onopen = () => {
        tentativa = 0;
        status.current?.(true);
      };

      socket.onmessage = (ev) => {
        try {
          handler.current(JSON.parse(ev.data) as EventoWs);
        } catch {
          // Frame inesperado não pode derrubar o painel.
        }
      };

      socket.onclose = () => {
        status.current?.(false);
        if (encerrado) return;
        // 1s, 2s, 4s... até 15s. Webview em segundo plano fecha o socket, e
        // reabrir em loop apertado só gastaria bateria.
        const espera = Math.min(15_000, 1000 * 2 ** tentativa);
        tentativa += 1;
        timer = window.setTimeout(conectar, espera);
      };

      socket.onerror = () => socket?.close();
    };

    conectar();

    return () => {
      encerrado = true;
      if (timer) window.clearTimeout(timer);
      socket?.close();
    };
  }, [ativo, token, tenant]);
}
